import React, { useState, useCallback } from 'react';
import {
  SimpleForm,
  TextInput,
  ImageInput,
  BooleanInput,
  FormDataConsumer,
  SimpleShowLayout,
  required,
  SelectInput,
  useRecordContext,
  useNotify,
  useRefresh,
} from 'react-admin';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useFormContext } from 'react-hook-form';
import { withStyles } from '@mui/styles';
import ImgField from '../../../components/ImgField/ImgField';
import CueImagesList from '../../../components/CueImagesList/CueImagesList';
import VideoFileInput from '../../../components/VideoFileInput';
import ExercisesUpdateToolbar from './ExercisesEditToolbar';
import exerciseType from '../../../constants/exerciseType';
import uploadService from '../../../utils/upload';
import uploadPicture from '../../../utils/uploadPicture';
import ImagePickerButton from '../../../components/ImagePickerButton/ImagePickerButton';
import countUploading from '../../../utils/countUploading';
import dataProvider from '../../../providers/dataProvider';
import VideoPickerButton, { SelectedVideoDisplay } from '../../../components/VideoPickerButton';

const UPDATE = 'UPDATE';

const saveExercise = (id, values, prevValues) => {
  return dataProvider(UPDATE, 'exercise', { id, data: values, previousData: prevValues });
};

// Helper component to provide setValue to FormDataConsumer children
const FormDataConsumerWithSetValue = ({ children }) => {
  const formContext = useFormContext();
  // Provide a no-op function if form context isn't available yet
  const setValue = formContext?.setValue || (() => {});
  return <FormDataConsumer>{({ formData }) => children({ formData, setValue })}</FormDataConsumer>;
};

// Wrapper component for video picker that clears the file input
const VideoPickerWithFormClear = ({ onVideoSelect, formFieldToClear }) => {
  const formContext = useFormContext();

  const handleSelect = useCallback(
    (video) => {
      onVideoSelect(video);
      // Clear the file input when a video is selected from library
      if (formContext?.setValue && formFieldToClear) {
        formContext.setValue(formFieldToClear, null);
      }
    },
    [formContext, onVideoSelect, formFieldToClear],
  );

  return <VideoPickerButton onSelect={handleSelect} />;
};

const styles = {
  simpleShowLayout: {
    width: '100%',
    marginBottom: '20px',
    background: 'white',
    boxShadow:
      '0px 1px 3px 0px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
  input: {
    width: '100%',
  },
  inlineBlock: { display: 'inline-flex', marginRight: '1rem' },
};

const EditForm = ({ classes }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [state, setState] = useState({
    loading: false,
    progressImg: 0,
    progressCueImgs: 0,
  });
  const [videoUploading, setVideoUploading] = useState(false);
  const [fullVideoUploading, setFullVideoUploading] = useState(false);

  const [selectedLibraryVideo, setSelectedLibraryVideo] = useState(null);
  const [selectedLibraryFullVideo, setSelectedLibraryFullVideo] = useState(null);

  const progressCountImg = useCallback((progressEvent) => {
    setState((prev) => ({ ...prev, progressImg: countUploading(progressEvent) }));
  }, []);

  const progressCountCueImgs = useCallback((progressEvent) => {
    setState((prev) => ({ ...prev, progressCueImgs: countUploading(progressEvent) }));
  }, []);

  const uploadImg = useCallback((img) => uploadPicture(img, progressCountImg), [progressCountImg]);

  const uploadCueImage = useCallback(
    (img) => {
      if (img && typeof img.picture === 'object') {
        const formData = new FormData();
        formData.append('file', img.picture.rawFile);
        return uploadService(formData, progressCountCueImgs).then((res) => {
          return Promise.resolve({
            picture: res.data.fileName,
            description: img.description,
          });
        });
      }
      return Promise.resolve(img);
    },
    [progressCountCueImgs],
  );

  const uploadCueImages = useCallback(
    (cueImages) => {
      const damp = [...cueImages].map((req) => uploadCueImage(req));
      return Promise.all(damp).then((data) => {
        const images = data.map((img) => img);
        return Promise.resolve(images);
      });
    },
    [uploadCueImage],
  );

  const getTips = (values) => {
    const tips = [values.tip1, values.tip2, values.tip3];

    values.tips = [...tips.filter((t) => t)];
    return values;
  };

  const handleSubmit = useCallback(
    async (values) => {
      // newVideo / newFullVideo were uploaded on drop and reduced to { id, ... }
      // by VideoFileInput, so we can read the id directly here.
      const { video, fullVideo, newFullVideo, newVideo, ...cacheValues } = { ...values };
      setState((prev) => ({ ...prev, loading: true }));
      const originalPicture = record?.picture;
      try {
        const [imgResult, cueImagesResult] = await Promise.all([
          uploadImg(cacheValues.picture),
          uploadCueImages(cacheValues.cueImages || []),
        ]);
        cacheValues.picture = imgResult?.data?.fileName || originalPicture;

        const resolvedVideoId = selectedLibraryVideo?.id || newVideo?.id || video?.id || null;
        const resolvedFullVideoId =
          selectedLibraryFullVideo?.id || newFullVideo?.id || fullVideo?.id || null;
        if (resolvedVideoId) {
          cacheValues.videoId = resolvedVideoId;
        }
        cacheValues.fullVideoId = resolvedFullVideoId;

        cacheValues.cueImages = cueImagesResult;
        const tipped = getTips(cacheValues);
        tipped.countdownTimer = 3;
        await saveExercise(tipped.id, tipped, null);
        notify('Exercise saved successfully', { type: 'success' });
        refresh();
      } catch (error) {
        notify(error?.message || 'Failed to save exercise', { type: 'error' });
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [
      record?.picture,
      uploadImg,
      uploadCueImages,
      notify,
      refresh,
      selectedLibraryVideo,
      selectedLibraryFullVideo,
    ],
  );

  if (!record) {
    return null;
  }

  const recordWithoutRelation = { ...record };
  delete recordWithoutRelation.relation;

  const { video, type, fullVideo } = recordWithoutRelation;
  const { loading, progressImg, progressCueImgs } = state;
  const anyUploading = videoUploading || fullVideoUploading;

  return (
    <SimpleForm
      defaultValues={{
        ...recordWithoutRelation,
        newVideo: video,
        newFullVideo: fullVideo,
      }}
      onSubmit={handleSubmit}
      toolbar={
        <ExercisesUpdateToolbar
          loading={loading || anyUploading}
          uploading={anyUploading}
          progressImg={progressImg}
          progressCueImgs={progressCueImgs}
        />
      }
    >
      <SimpleShowLayout className={classes.simpleShowLayout}>
        <TextInput source="name" className={classes.input} validate={required()} />
        <ImageInput
          validate={required()}
          className={classes.inlineBlock}
          source="picture"
          format={(v) => (typeof v === 'string' ? { picture: v } : v)}
          label="Exercise Photo"
          accept={{ 'image/*': [] }}
        >
          <ImgField width="100" height="100" source="picture" />
        </ImageInput>
        <ImagePickerButton source="picture" label={false} />

        <VideoFileInput
          source="newVideo"
          label={
            type === exerciseType.lesson ? 'Lesson video (upload new)' : 'Demo video (upload new)'
          }
          validate={required()}
          className={classes.inlineBlock}
          values={record}
          checkedName="video"
          checkedResource="exercise"
          onUploadingChange={setVideoUploading}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Or select an existing video from the library:
          </Typography>
          <SelectedVideoDisplay
            video={selectedLibraryVideo}
            onClear={() => setSelectedLibraryVideo(null)}
          />
          <VideoPickerWithFormClear
            onVideoSelect={setSelectedLibraryVideo}
            formFieldToClear="newVideo"
          />
        </Box>

        <TextInput source="description" label="Description" validate={required()} />
        <SelectInput
          label="Exercise Type"
          source="type"
          choices={[
            { id: 1, name: exerciseType[1] },
            { id: 2, name: exerciseType[2] },
            { id: 3, name: exerciseType[3] },
          ]}
        />
        <BooleanInput label="Ask user to switch sides and repeat exercise" source="forEachSide" />
        <BooleanInput label="Hide done button in timer" source="doneButton" />
      </SimpleShowLayout>

      {type !== exerciseType.lesson && (
        <SimpleShowLayout className={classes.simpleShowLayout}>
          <>
            <Typography variant="title" component="h4">
              Tutorial video
            </Typography>
            <Typography component="span">
              Longest version of the video should be uploaded here. If no video is uploaded in this
              session, the same video uploaded above will be shown.
            </Typography>
          </>
          <VideoFileInput
            source="newFullVideo"
            label="Upload new"
            values={record}
            checkedName="fullVideo"
            checkedResource="exercise"
            onUploadingChange={setFullVideoUploading}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Or select an existing video from the library:
            </Typography>
            <SelectedVideoDisplay
              video={selectedLibraryFullVideo}
              onClear={() => setSelectedLibraryFullVideo(null)}
            />
            <VideoPickerWithFormClear
              onVideoSelect={setSelectedLibraryFullVideo}
              formFieldToClear="newFullVideo"
            />
          </Box>
        </SimpleShowLayout>
      )}
      <SimpleShowLayout className={classes.simpleShowLayout}>
        <FormDataConsumerWithSetValue>
          {({ formData, setValue }) => {
            if (formData && formData.cueImages) {
              return (
                <CueImagesList
                  source="cueImages"
                  values={formData.cueImages}
                  handleData={(value) => {
                    setValue('cueImages', value);
                    setValue('imagesForCue', null);
                  }}
                />
              );
            }
            return null;
          }}
        </FormDataConsumerWithSetValue>
      </SimpleShowLayout>
      <SimpleShowLayout className={classes.simpleShowLayout}>
        <FormDataConsumer>
          {(data) => {
            if (data.formData && data.formData.tips) {
              return (
                <>
                  <Typography variant="title" component="h4">
                    On screen tips
                  </Typography>
                  <Typography component="span">
                    These tips appear in timer and repetition counter screens to help guide user as
                    their doing the exercise.
                  </Typography>
                  <TextInput source="tip1" defaultValue={data.formData.tips[0]} label="" />
                  <TextInput source="tip2" defaultValue={data.formData.tips[1]} label="" />
                  <TextInput source="tip3" defaultValue={data.formData.tips[2]} label="" />
                </>
              );
            }
            return null;
          }}
        </FormDataConsumer>
      </SimpleShowLayout>
    </SimpleForm>
  );
};

export default withStyles(styles)(EditForm);
